export const FOLDER_OR_DOCUMENT_NAME_MAX_CODEPOINTS = 255;

/**
 * Character-level allow-list shared with the backend FolderOrDocumentName value object after NFC
 * normalization and trimming. Reserved DOS device names and trailing-dot normalization are
 * handled separately because regex alone is a poor fit for those filesystem rules.
 */
export const FOLDER_OR_DOCUMENT_NAME_ALLOWED_PATTERN = /^[^\u0000-\u001F\u007F\\/<>|:?*"]+$/u;

const RESERVED_FOLDER_OR_DOCUMENT_NAMES = new Set([
  '.',
  '..',
  'con',
  'prn',
  'aux',
  'nul',
  ...Array.from({ length: 9 }, (_, idx) => `com${idx + 1}`),
  ...Array.from({ length: 9 }, (_, idx) => `lpt${idx + 1}`),
]);

export type FolderOrDocumentNameInvalidReason =
  | 'empty_after_trim'
  | 'reserved_name'
  | 'length_exceeded'
  | 'forbidden_codepoint';

export interface FolderOrDocumentNameValidationResult {
  readonly valid: boolean;
  readonly value: string;
  readonly reason?: FolderOrDocumentNameInvalidReason;
}

export class FolderOrDocumentNameValidationError extends Error {
  constructor(
    readonly reason: FolderOrDocumentNameInvalidReason,
    readonly input: string,
  ) {
    super(reason);
  }
}

export function validateFolderOrDocumentName(rawInput: string): FolderOrDocumentNameValidationResult {
  const normalized = rawInput.normalize('NFC');
  const whitespaceTrimmed = normalized.trim();
  if (!whitespaceTrimmed) {
    return invalid('empty_after_trim', whitespaceTrimmed);
  }
  if (RESERVED_FOLDER_OR_DOCUMENT_NAMES.has(whitespaceTrimmed.toLocaleLowerCase())) {
    return invalid('reserved_name', whitespaceTrimmed);
  }

  const trimmed = whitespaceTrimmed.replace(/\.+$/u, '').trim();
  if (!trimmed) {
    return invalid('empty_after_trim', trimmed);
  }
  if (RESERVED_FOLDER_OR_DOCUMENT_NAMES.has(trimmed.toLocaleLowerCase())) {
    return invalid('reserved_name', trimmed);
  }
  if (Array.from(trimmed).length > FOLDER_OR_DOCUMENT_NAME_MAX_CODEPOINTS) {
    return invalid('length_exceeded', trimmed);
  }
  if (!FOLDER_OR_DOCUMENT_NAME_ALLOWED_PATTERN.test(trimmed)) {
    return invalid('forbidden_codepoint', trimmed);
  }

  return { valid: true, value: trimmed };
}

export function requireValidFolderOrDocumentName(rawInput: string): string {
  const result = validateFolderOrDocumentName(rawInput);
  if (!result.valid) {
    throw new FolderOrDocumentNameValidationError(result.reason ?? 'forbidden_codepoint', rawInput);
  }
  return result.value;
}

export function folderOrDocumentNameErrorKey(reason: FolderOrDocumentNameInvalidReason | undefined): string {
  switch (reason) {
    case 'length_exceeded':
      return 'DOCUMENT_MANAGEMENT.PROBLEM.INVALID_NAME_LENGTH';
    case 'forbidden_codepoint':
      return 'DOCUMENT_MANAGEMENT.PROBLEM.INVALID_NAME_CHARS';
    case 'reserved_name':
      return 'DOCUMENT_MANAGEMENT.PROBLEM.INVALID_NAME_RESERVED';
    case 'empty_after_trim':
    default:
      return 'DOCUMENT_MANAGEMENT.PROBLEM.INVALID_NAME_EMPTY';
  }
}

function invalid(
  reason: FolderOrDocumentNameInvalidReason,
  value: string,
): FolderOrDocumentNameValidationResult {
  return { valid: false, value, reason };
}
